/**
 * DuplicateDecisionButtons — Phase R10.4 (2026-07-05)
 *
 * Renders when claim.dedup_flag ∈ {yellow, orange}
 * AND claim.owner_duplicate_decision is null.
 *
 * Two buttons side-by-side (below <DuplicateComparisonPanel />):
 *   - "Confirm Different Transaction" (secondary / outlined)
 *     opens a modal with optional reason textarea, POSTs
 *     /confirm-different-transaction, surfaces toast + refetch.
 *   - "Confirm Duplicate — Reject Claim" (danger / red)
 *     opens a modal with REQUIRED reason textarea, POSTs
 *     /confirm-duplicate, surfaces toast + refetch.
 *
 * If claim.owner_duplicate_decision ∈ {"confirmed_different",
 * "confirmed_duplicate"}, the buttons are replaced by a small
 * read-only info strip.
 *
 * Props:
 *   claim                            — full claim object
 *   onConfirmedDifferent(reason)     — mutation handler, parent fires POST
 *   onConfirmedDuplicate(reason)     — mutation handler
 *   pendingDifferent                 — bool
 *   pendingDuplicate                 — bool
 */
import { useState } from 'react';
import {
  CheckCircle2, XCircle, Loader2, X, AlertTriangle,
} from 'lucide-react';

const REASON_MAX = 500;

export default function DuplicateDecisionButtons({
  claim,
  onConfirmedDifferent,
  onConfirmedDuplicate,
  pendingDifferent = false,
  pendingDuplicate = false,
}) {
  const [modal, setModal] = useState(null); // null | "different" | "duplicate"

  const flag = (claim?.dedup_flag || 'green').toLowerCase();
  const decision = claim?.owner_duplicate_decision ?? null;

  // Already-decided → render NOTHING here. The parent page is now
  // responsible for surfacing the DecidedStrip at a page-level location
  // that remains visible regardless of flag/match state (R10.4b scope 1).
  if (decision === 'confirmed_different' || decision === 'confirmed_duplicate') {
    return null;
  }
  // Not yellow / orange → buttons not applicable
  if (flag !== 'yellow' && flag !== 'orange') {
    return null;
  }

  const close = () => setModal(null);

  return (
    <>
      <div
        className="flex flex-col sm:flex-row gap-3"
        data-testid="duplicate-decision-buttons"
      >
        <button
          type="button"
          onClick={() => setModal('different')}
          disabled={pendingDifferent || pendingDuplicate}
          data-testid="duplicate-decision-confirm-different-btn"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-transparent border border-emerald-500/40 text-emerald-300 font-semibold rounded-2xl px-5 py-3 text-sm hover:bg-emerald-500/10 disabled:opacity-50 transition-colors min-h-[48px]"
        >
          <CheckCircle2 size={16} />
          Confirm Different Transaction
        </button>
        <button
          type="button"
          onClick={() => setModal('duplicate')}
          disabled={pendingDifferent || pendingDuplicate}
          data-testid="duplicate-decision-confirm-duplicate-btn"
          className="flex-1 inline-flex items-center justify-center gap-2 bg-transparent border border-red-500/40 text-red-300 font-semibold rounded-2xl px-5 py-3 text-sm hover:bg-red-500/10 disabled:opacity-50 transition-colors min-h-[48px]"
        >
          <XCircle size={16} />
          Confirm Duplicate
        </button>
      </div>

      {modal === 'different' ? (
        <ReasonModal
          testIDPrefix="confirm-different-modal"
          title="Confirm Different Transaction"
          intro="You are confirming that this receipt represents a different transaction from the matching claim. The claim will proceed to normal approval validation."
          requiredReason={false}
          submitLabel="Confirm Different Transaction"
          submitClass="bg-emerald-500 hover:bg-emerald-400 text-black"
          submitting={pendingDifferent}
          onClose={close}
          onSubmit={(reason) => {
            onConfirmedDifferent(reason);
            close();
          }}
        />
      ) : null}

      {modal === 'duplicate' ? (
        <ReasonModal
          testIDPrefix="confirm-duplicate-modal"
          title="Confirm Duplicate — Reject Claim"
          intro="This will reject the claim with reason 'confirmed_duplicate'. Zero stamps will be awarded. The owner can reopen the claim later if needed."
          requiredReason
          submitLabel="Reject as Duplicate"
          submitClass="bg-red-500 hover:bg-red-400 text-white"
          submitting={pendingDuplicate}
          onClose={close}
          onSubmit={(reason) => {
            onConfirmedDuplicate(reason);
            close();
          }}
          danger
        />
      ) : null}
    </>
  );
}

export function DecidedStrip({ claim, decision }) {
  const by = claim?.owner_duplicate_decision_by;
  const at = claim?.owner_duplicate_decision_at;
  const atLabel = at ? new Date(at).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';
  const isDifferent = decision === 'confirmed_different';
  const Icon = isDifferent ? CheckCircle2 : XCircle;
  const cls = isDifferent
    ? 'border-emerald-500/35 bg-emerald-500/10 text-emerald-100'
    : 'border-red-500/35 bg-red-500/10 text-red-100';
  const accent = isDifferent ? 'text-emerald-300' : 'text-red-300';
  const label = isDifferent
    ? 'Confirmed as different transaction'
    : 'Confirmed as duplicate';

  return (
    <div
      className={`flex items-start gap-3 rounded-2xl border px-4 py-3 ${cls}`}
      data-testid={`duplicate-decision-decided-strip-${isDifferent ? 'different' : 'duplicate'}`}
    >
      <Icon size={16} className={`${accent} mt-0.5 shrink-0`} />
      <div className="text-sm leading-relaxed">
        <span className="font-semibold">{label}</span>
        <span className="text-white/60">
          {' '}by{' '}
          <span className="font-mono text-xs" data-testid="duplicate-decision-decided-by">
            {by ? `${by.slice(0, 8)}…` : 'unknown'}
          </span>
          {' '}on{' '}
          <span data-testid="duplicate-decision-decided-at">{atLabel}</span>
        </span>
        {claim?.owner_duplicate_decision_reason ? (
          <div className="mt-1 text-xs text-white/70" data-testid="duplicate-decision-decided-reason">
            Reason: {claim.owner_duplicate_decision_reason}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ReasonModal({
  testIDPrefix, title, intro, requiredReason, submitLabel, submitClass,
  submitting, onClose, onSubmit, danger = false,
}) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const canSubmit = !submitting && (requiredReason ? trimmed.length > 0 : true);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      data-testid={testIDPrefix}
      onClick={submitting ? undefined : onClose}
    >
      <div
        className="w-full max-w-lg rounded-3xl border border-white/10 bg-[#15151B] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-gold mb-1">
              Decision
            </div>
            <h2 className="text-xl font-bold text-white">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid={`${testIDPrefix}-close`}
            className="p-1 rounded-lg text-white/50 hover:text-white hover:bg-white/5 disabled:opacity-40"
          >
            <X size={16} />
          </button>
        </div>

        {danger ? (
          <div
            className="flex items-start gap-2 rounded-xl border border-red-500/30 bg-red-500/5 p-3 mb-4 text-xs text-red-100"
            data-testid={`${testIDPrefix}-warn`}
          >
            <AlertTriangle size={14} className="text-red-300 mt-0.5 shrink-0" />
            <span>This action rejects the claim and awards 0 stamps.</span>
          </div>
        ) : null}

        <p className="text-sm text-white/75 leading-relaxed mb-4">{intro}</p>

        <label className="block mb-2 text-xs font-semibold text-white/70">
          Reason {requiredReason ? <span className="text-red-300">*</span> : <span className="text-white/40">(optional, for audit)</span>}
          <span className="text-white/40 font-normal ml-2">
            {reason.length}/{REASON_MAX}
          </span>
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value.slice(0, REASON_MAX))}
          rows={3}
          placeholder={requiredReason ? 'Explain why this is a duplicate (required)' : 'Why is this a different transaction?'}
          data-testid={`${testIDPrefix}-reason-input`}
          className="w-full rounded-xl bg-white/5 border border-white/10 px-3 py-2 text-sm text-white placeholder-white/35 focus:border-gold/60 focus:outline-none resize-none"
        />

        <div className="flex items-center justify-end gap-2 pt-5">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-sm text-white/70 hover:text-white disabled:opacity-40"
            data-testid={`${testIDPrefix}-cancel`}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => canSubmit && onSubmit(trimmed || null)}
            disabled={!canSubmit}
            data-testid={`${testIDPrefix}-submit`}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl disabled:opacity-40 disabled:cursor-not-allowed ${submitClass}`}
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : null}
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
